/**
 * Pontuação leve de força de senha (E13.F3.T04). // ux-neutrality:allow doc-comment
 *
 * Right-size-infra: não adicionamos `zxcvbn-ts` (~400KB minified) só para um
 * indicador visual em uma tela de signup. A heurística aqui aproxima o
 * comportamento esperado pelo usuário (penaliza repetição, premia variedade)
 * sem custo de bundle.
 *
 * Saída em 4 níveis: weak | medium | strong | veryStrong. Usada para escolher
 * cor da barra e rótulo i18n; nunca aparece em validação de submit
 * (server-side faz isso). Cap em comprimento ≥ 8 mantido como hard-min.
 */

export type PasswordStrengthLevel = 'weak' | 'medium' | 'strong' | 'veryStrong';

export interface PasswordStrengthScore {
  level: PasswordStrengthLevel;
  percent: number;
}

const MIN_LEN = 8;
const STRONG_LEN = 12;
const VERY_STRONG_LEN = 16;

const TRIVIAL_FULL = new Set([
  'password',
  'senha',
  '12345678',
  '123456789',
  'qwertyui',
  'abcdefgh',
  '00000000',
]);

function countVariety(password: string): number {
  return [
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /\d/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ].filter(Boolean).length;
}

function rateLong(password: string, variety: number): PasswordStrengthScore | null {
  if (password.length >= VERY_STRONG_LEN && variety >= 3) {
    return { level: 'veryStrong', percent: 100 };
  }
  if (password.length >= STRONG_LEN && variety >= 3) {
    return { level: 'strong', percent: 80 };
  }
  return null;
}

export function scorePasswordStrength(password: string): PasswordStrengthScore {
  if (password.length === 0) return { level: 'weak', percent: 0 };
  if (password.length < MIN_LEN || TRIVIAL_FULL.has(password.toLowerCase())) {
    return { level: 'weak', percent: 25 };
  }
  const variety = countVariety(password);
  if (/(.)\1{3,}/.test(password) || variety <= 1) {
    return { level: 'weak', percent: 30 };
  }
  const long = rateLong(password, variety);
  if (long !== null) return long;
  if (variety >= 2) return { level: 'medium', percent: 55 };
  return { level: 'weak', percent: 35 };
}
